import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Menu, X, Home, Search, Building2, BookOpen, Info, LogOut, User } from "lucide-react";
import { User as SupabaseUser } from "@supabase/supabase-js";

interface MobileNavProps {
  user: SupabaseUser | null;
  onLogout: () => void;
  onStartFundraiser: () => void;
  onDashboardClick: () => void;
}

export const MobileNav = ({ user, onLogout, onStartFundraiser, onDashboardClick }: MobileNavProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const navItems = [
    { label: "Home", href: "/", icon: Home },
    { label: "Explore Fundraisers", href: "/explore", icon: Search },
    { label: "Organizations", href: "/organizations", icon: Building2 },
    { label: "Stories", href: "/stories", icon: BookOpen },
    { label: "About", href: "/about", icon: Info },
  ];

  const handleNavClick = (href: string) => {
    navigate(href);
    setIsOpen(false);
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="lg:hidden h-10 w-10">
          <Menu className="h-6 w-6" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[300px] sm:w-[350px] p-0">
        <SheetHeader className="p-4 border-b border-border">
          <SheetTitle className="text-left">Menu</SheetTitle>
        </SheetHeader>
        
        <nav className="flex flex-col p-4">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.href}
                onClick={() => handleNavClick(item.href)}
                className="flex items-center gap-3 px-4 py-3 text-foreground hover:bg-secondary rounded-lg transition-colors text-left"
              >
                <Icon className="w-5 h-5 text-muted-foreground" />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
          
          <div className="my-4 border-t border-border" />
          
          {user ? (
            <>
              <button
                onClick={() => {
                  onDashboardClick();
                  setIsOpen(false);
                }}
                className="flex items-center gap-3 px-4 py-3 text-foreground hover:bg-secondary rounded-lg transition-colors text-left"
              >
                <User className="w-5 h-5 text-muted-foreground" />
                <span className="font-medium">Dashboard</span>
              </button>
              <button
                onClick={() => {
                  onLogout();
                  setIsOpen(false);
                }}
                className="flex items-center gap-3 px-4 py-3 text-foreground hover:bg-secondary rounded-lg transition-colors text-left"
              >
                <LogOut className="w-5 h-5 text-muted-foreground" />
                <span className="font-medium">Logout</span>
              </button>
            </>
          ) : (
            <>
              <Button
                onClick={() => {
                  navigate('/auth');
                  setIsOpen(false);
                }}
                variant="outline"
                className="w-full mb-2"
              >
                Sign In
              </Button>
              <Button
                onClick={() => {
                  onStartFundraiser();
                  setIsOpen(false);
                }}
                className="w-full"
              >
                Start Fundraiser
              </Button>
            </>
          )}
        </nav>
      </SheetContent>
    </Sheet>
  );
};
